import { API_ROOT } from '@Helpers/apiConfig';
import { TOKEN_REFRESH_SUCCESS, LOGOUT_SUCCESS } from '@Constants/actionTypes';

/**
 * Single source of truth for refreshing the access token.
 *
 * The app runs two independent data-fetching stacks side by side: the legacy
 * redux middleware (src/helpers/api.js) and the tanstack/ky client
 * (src/helpers/pacerApi.ts). When the access token expires while requests from
 * both stacks are in flight, both would 401 and both would POST
 * account/refresh-token with the SAME refresh token.
 *
 * Identity Server issues refresh tokens as RefreshTokenUsage.OneTimeOnly
 * (pacer-identity HostingExtensions.cs). Under concurrency the first refresh
 * consumes the token and the second gets `invalid_grant` — logging the user out
 * of a perfectly valid session at random (confirmed against test-app: two
 * concurrent refreshes with one token return 200 + 401).
 *
 * This module removes the race at its source: BOTH stacks call
 * refreshAccessToken(store), which keeps a single in-flight promise. Concurrent
 * callers await the same POST instead of firing their own. The refresh token is
 * always read from the store at call time, so a caller that arrives just after a
 * refresh completed picks up the freshly-rotated token rather than a spent one.
 */

// Shared across every importer of this module — the guarantee that makes the
// two stacks cooperate instead of racing.
let _refreshPromise: Promise<string | null> | null = null;

interface RefreshableStore {
    getState: () => { user: { refreshToken?: string; isSSO?: boolean } };
    dispatch: (action: { type: string; payload?: unknown }) => unknown;
}

export const refreshAccessToken = (store: RefreshableStore): Promise<string | null> => {
    if (_refreshPromise) return _refreshPromise;

    const { refreshToken, isSSO } = store.getState().user;

    // No refresh token to trade in — the session is over.
    if (!refreshToken) {
        store.dispatch({ type: LOGOUT_SUCCESS });
        return Promise.resolve(null);
    }

    _refreshPromise = fetch(`${API_ROOT}account/refresh-token`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refreshToken, isSSO }),
    })
        .then(async (resp) => {
            if (!resp.ok) throw new Error('Token refresh failed');
            const data = await resp.json();
            // Rotates both accessToken and refreshToken in the store so the next
            // caller reads the fresh refresh token, not the consumed one.
            store.dispatch({ type: TOKEN_REFRESH_SUCCESS, payload: data });
            return data.accessToken as string;
        })
        .catch(() => {
            // Refresh genuinely failed (expired / revoked): end the session.
            // resetEnhancer clears redux on LOGOUT_SUCCESS; PrivateRoute redirects.
            store.dispatch({ type: LOGOUT_SUCCESS });
            return null;
        })
        .finally(() => {
            _refreshPromise = null;
        });

    return _refreshPromise;
};

// Test-only: reset the shared in-flight promise between cases.
export const __resetRefreshPromiseForTests = (): void => {
    _refreshPromise = null;
};
